function removerTitleNativo(elemento: Element) {
  if (!elemento.hasAttribute("title")) return;
  const valor = elemento.getAttribute("title");
  if (valor) {
    elemento.setAttribute("data-title-bloqueado", valor);
    const ehCampo = elemento.matches("input, textarea, select, option, [contenteditable='true']");
    if (!ehCampo) {
      /* Compatibilidade para código antigo: qualquer `title` que escapar é
         promovido ao tooltip visual da plataforma antes de ser removido. */
      if (!elemento.hasAttribute("data-tooltip")) elemento.setAttribute("data-tooltip", valor);
      elemento.classList.add("tooltip");
      if (!elemento.hasAttribute("aria-label") && elemento.matches("button, a, iframe, [role='button']")) {
        elemento.setAttribute("aria-label", valor);
      }
    }
  }
  elemento.removeAttribute("title");
}

function removerTitlesDaArvore(raiz: ParentNode) {
  if (raiz instanceof Element) removerTitleNativo(raiz);
  raiz.querySelectorAll?.("[title]").forEach(removerTitleNativo);
}

const SELETOR_CAMPO_TEXTO = "input:not([type='hidden']), textarea";
const SELETOR_CAMPO_VALIDAVEL = "input, textarea, select";
const IDENTIFICADOR_SESSAO = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
let sequenciaCampos = 0;
const nomesCamposProtegidos = new WeakMap<Element, string>();

function definirAtributoSeNecessario(elemento: Element, nome: string, valor: string) {
  if (elemento.getAttribute(nome) !== valor) elemento.setAttribute(nome, valor);
}

function obterNomeProtegido(elemento: Element) {
  const existente = nomesCamposProtegidos.get(elemento);
  if (existente) return existente;
  sequenciaCampos += 1;
  const nome = `i9-sem-autofill-${IDENTIFICADOR_SESSAO}-${sequenciaCampos}`;
  nomesCamposProtegidos.set(elemento, nome);
  return nome;
}

/**
 * Isola cada campo em uma seção inexistente de formulário. Além do nome
 * opaco, isso impede o Chromium de reaproveitar a classificação de endereço,
 * contato ou pagamento aprendida para outro campo da plataforma.
 */
function obterAutocompleteProtegido(elemento: Element) {
  const secao = obterNomeProtegido(elemento).replace(/[^a-z0-9]/gi, "").toLowerCase();
  const tipo = elemento instanceof HTMLInputElement ? elemento.type.toLowerCase() : "text";
  const finalidade = tipo === "password" ? "new-password" : "one-time-code";
  return `section-${secao} ${finalidade}`;
}

function bloquearSugestoesNativas(elemento: Element) {
  if (!(elemento instanceof HTMLInputElement || elemento instanceof HTMLTextAreaElement)) return;
  if (elemento instanceof HTMLInputElement && elemento.type === "hidden") return;

  // O Chrome pode ignorar autocomplete="off" e consultar o historico pelo
  // atributo name. Um nome opaco, unico por carregamento e por campo impede
  // que o navegador associe o input aos valores antigos sem afetar o estado
  // React, o valor, o foco ou a digitacao.
  definirAtributoSeNecessario(elemento, "name", obterNomeProtegido(elemento));
  elemento.removeAttribute("list");
  // "off" nao serve aqui: o autofill de enderecos/contatos do Chrome ignora
  // esse valor. Ver `autoCompleteBloqueado`.
  definirAtributoSeNecessario(
    elemento,
    "autocomplete",
    obterAutocompleteProtegido(elemento)
  );
  definirAtributoSeNecessario(elemento, "aria-autocomplete", "none");
  definirAtributoSeNecessario(elemento, "writingsuggestions", "false");
  definirAtributoSeNecessario(elemento, "spellcheck", "false");
  definirAtributoSeNecessario(elemento, "autocorrect", "off");
  definirAtributoSeNecessario(elemento, "autocapitalize", "off");
  definirAtributoSeNecessario(elemento, "data-form-type", "other");
  definirAtributoSeNecessario(elemento, "data-lpignore", "true");
  definirAtributoSeNecessario(elemento, "data-1p-ignore", "true");
  definirAtributoSeNecessario(elemento, "data-bwignore", "true");
  definirAtributoSeNecessario(elemento, "data-gramm", "false");
  definirAtributoSeNecessario(elemento, "data-gramm_editor", "false");
  definirAtributoSeNecessario(elemento, "data-enable-grammarly", "false");
  definirAtributoSeNecessario(elemento, "data-ms-editor", "false");
}

/** Mantém a obrigatoriedade acessível, mas elimina a validação visual nativa. */
function bloquearValidacaoNativa(elemento: Element) {
  if (!elemento.matches(SELETOR_CAMPO_VALIDAVEL)) return;
  if (elemento.hasAttribute("required")) {
    definirAtributoSeNecessario(elemento, "aria-required", "true");
    elemento.removeAttribute("required");
  }
}

/**
 * Desliga a validacao nativa do formulario. Sem `novalidate` o navegador
 * interrompe o envio e abre a bolha "Preencha este campo." antes de o React
 * receber o submit; a plataforma ja valida em JS e mostra o erro no proprio
 * campo, entao a checagem do navegador so atrapalha.
 */
function prepararFormulario(formulario: HTMLFormElement) {
  definirAtributoSeNecessario(formulario, "autocomplete", "off");
  definirAtributoSeNecessario(formulario, "novalidate", "");
}

function bloquearAssistenciasDaArvore(raiz: ParentNode) {
  removerTitlesDaArvore(raiz);
  if (raiz instanceof Element && raiz.matches(SELETOR_CAMPO_TEXTO)) {
    bloquearSugestoesNativas(raiz);
  }
  raiz.querySelectorAll?.(SELETOR_CAMPO_TEXTO).forEach(bloquearSugestoesNativas);
  if (raiz instanceof Element) bloquearValidacaoNativa(raiz);
  raiz.querySelectorAll?.(SELETOR_CAMPO_VALIDAVEL).forEach(bloquearValidacaoNativa);
  if (raiz instanceof HTMLFormElement) prepararFormulario(raiz);
  raiz.querySelectorAll?.("form").forEach(prepararFormulario);
}

/**
 * Bloqueia tooltips, sugestões de dados salvos e autopreenchimento nativos em
 * toda a plataforma. `title` legado é convertido em `data-tooltip`; campos
 * nunca exibem balão, pois erros pertencem ao componente visual e ao toast.
 */
export function bloquearTooltipsNativos() {
  if (typeof document === "undefined") return;

  definirAtributoSeNecessario(document.documentElement, "writingsuggestions", "false");
  definirAtributoSeNecessario(document.documentElement, "spellcheck", "false");
  definirAtributoSeNecessario(document.documentElement, "autocorrect", "off");
  definirAtributoSeNecessario(document.documentElement, "autocapitalize", "off");
  bloquearAssistenciasDaArvore(document);

  const removerDoEvento = (evento: Event) => {
    const alvo = evento.target;
    if (!(alvo instanceof Element)) return;
    const elementoComTitle = alvo.closest("[title]");
    if (elementoComTitle) removerTitleNativo(elementoComTitle);
    if (alvo.matches(SELETOR_CAMPO_TEXTO)) bloquearSugestoesNativas(alvo);
    bloquearValidacaoNativa(alvo);
    const formulario = alvo.closest("form");
    if (formulario) prepararFormulario(formulario);
  };

  document.addEventListener("pointerover", removerDoEvento, true);
  document.addEventListener("pointerdown", removerDoEvento, true);
  document.addEventListener("mouseover", removerDoEvento, true);
  document.addEventListener("focusin", removerDoEvento, true);

  /* Balão de validação nativa ("Preencha este campo.", "Formato inválido"...):
     a plataforma já mostra o erro no próprio campo e no toast, com o texto
     traduzido. Cancelar o evento `invalid` esconde a bolha do navegador sem
     mudar a validação em si — um campo obrigatório vazio continua impedindo o
     envio, só não abre o tooltip. Vale para todo input/select/textarea da
     plataforma, inclusive os que ainda venham a ser criados. */
  document.addEventListener("invalid", (evento) => evento.preventDefault(), true);

  const observer = new MutationObserver((mutacoes) => {
    mutacoes.forEach((mutacao) => {
      if (mutacao.type === "attributes" && mutacao.target instanceof Element) {
        if (mutacao.attributeName === "title") removerTitleNativo(mutacao.target);
        if (mutacao.target.matches(SELETOR_CAMPO_TEXTO)) bloquearSugestoesNativas(mutacao.target);
        bloquearValidacaoNativa(mutacao.target);
        if (mutacao.target instanceof HTMLFormElement) prepararFormulario(mutacao.target);
        return;
      }
      mutacao.addedNodes.forEach((node) => {
        if (node instanceof Element || node instanceof DocumentFragment) {
          bloquearAssistenciasDaArvore(node);
        }
      });
    });
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: [
      "title",
      "name",
      "autocomplete",
      "novalidate",
      "required",
      "aria-autocomplete",
      "writingsuggestions",
      "spellcheck",
      "autocorrect",
      "autocapitalize",
    ],
    childList: true,
    subtree: true,
  });
}
